/**
 * Format raw OAuth token endpoint response into the same shape as
 * formatCognitoUserData.js — this contract must not change.
 *
 * Decodes the id_token JWT payload using only atob() — no external library.
 * The signature is NOT verified here; Cognito already validated it at the
 * token endpoint. This function only extracts claims for the Vuex store.
 *
 * Output shape (must match formatCognitoUserData.js exactly):
 *   {
 *     idToken, accessToken, refreshToken, exp,
 *     family_name, given_name, email, birthdate,
 *     onboardingcomplete, username, identities, isFirstLogin
 *   }
 */

/**
 * Decode a base64url-encoded string to a UTF-8 string.
 * Handles the standard base64url → base64 conversion before calling atob().
 *
 * @param {string} base64url
 * @returns {string}
 */
function decodeBase64url(base64url) {
  // Re-pad to a multiple of 4 and convert base64url chars to standard base64
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

  // atob returns a binary string; decode to UTF-8 via percent-encoding
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Extract and parse the payload segment of a JWT.
 *
 * @param {string} jwt - A JWT string in header.payload.signature format
 * @returns {object} Parsed JSON payload
 * @throws {Error} When the JWT is malformed or the payload is not valid JSON
 */
function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== 'string') {
    throw new Error('formatOAuthTokenData: id_token must be a non-empty string');
  }

  const segments = jwt.split('.');
  if (segments.length !== 3) {
    throw new Error(
      `formatOAuthTokenData: id_token has ${segments.length} segment(s); expected 3 (header.payload.signature)`
    );
  }

  const payloadSegment = segments[1];
  if (!payloadSegment) {
    throw new Error('formatOAuthTokenData: id_token payload segment is empty');
  }

  try {
    const decoded = decodeBase64url(payloadSegment);
    return JSON.parse(decoded);
  } catch (err) {
    throw new Error(`formatOAuthTokenData: failed to decode id_token payload — ${err.message}`);
  }
}

/**
 * Convert an OAuth token response into the canonical user data shape
 * used throughout KeeperFA (Vuex store, beforeEach guard, CRM sync).
 *
 * @param {object} tokens - Raw response from the Cognito token endpoint
 * @param {string} tokens.access_token
 * @param {string} tokens.id_token
 * @param {string} tokens.refresh_token
 * @returns {object} Formatted user data matching formatCognitoUserData.js output
 */
export default function formatOAuthTokenData({ access_token, id_token, refresh_token }) {
  const payload = decodeJwtPayload(id_token);

  let identities = [];
  if (payload.identities && payload.identities.length) {
    identities = payload.identities.map((identity) => identity.providerType);
  }

  return {
    idToken: id_token,
    accessToken: access_token,
    refreshToken: refresh_token,
    exp: payload.exp,
    family_name: payload.family_name,
    given_name: payload.given_name,
    email: payload.email,
    birthdate: payload.birthdate,
    // "onboardingcomplete" is used only for user redirect back to the drupal site
    // (value from CRM is used on the application pages) — matches formatCognitoUserData.js
    onboardingcomplete: payload['custom:onboarding_status'],
    username: payload.sub,
    identities,
    isFirstLogin: payload['custom:is_first_login'],
  };
}
